/**
 * ============================================================================
 * The outcomes rail — ONE definition, two consumers.
 * ============================================================================
 *
 * Extracted 2026-08-23 (D-075) when the Home teaser gained an expandable panel
 * that shows the same outcomes the `/products` card shows. The markup existed
 * once at that point; leaving it to exist twice is how a marker, a colour role
 * or a text size drifts between two pages that are supposed to be showing the
 * same record. D-061 found exactly that already in this repo — Services and
 * Education both carried the card treatment as duplicated Tailwind and **the
 * two copies had already diverged by an `h-full`**.
 *
 * THE TOP MARGIN IS THE ONLY THING A CALLER DECIDES, and it is a prop rather
 * than a constant because the two contexts are genuinely different: on
 * `/products` the list follows a description inside a roomy three-column card,
 * in the teaser it follows one inside a quarter-width card under a rule. What
 * a caller must NOT be able to change is the marker, its colour role, the text
 * size or the spacing between rows — those are the things that would silently
 * diverge.
 */
interface ProductOutcomesProps {
	outcomes: readonly string[];
	/** Spacing above the list — the caller's layout, not this component's. */
	className: string;
}

export default function ProductOutcomes({ outcomes, className }: ProductOutcomesProps): JSX.Element | null {
	/* Guarded because an entry may legitimately carry none — II.8 says omit an
	   outcome rather than invent one, and SFT Docx takes that option. Without
	   the guard an empty `outcomes` still renders the <ul> and its top margin,
	   i.e. 20px of blank card where a claim would be. */
	if (outcomes.length === 0) return null;

	return (
		<ul className={`${className} space-y-2`}>
			{outcomes.map((outcome: string) => (
				<li key={outcome} className="flex gap-2 text-sm font-medium text-[var(--text-primary)]">
					<span aria-hidden="true" className="text-[var(--accent-marker)]">
						→
					</span>
					<span>{outcome}</span>
				</li>
			))}
		</ul>
	);
}
